import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { TEAM } from "../../data/content";

const CARD_ANIM = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5 },
};

/**
 * MeetTheSpecialists — subtle homepage strip highlighting the two named
 * clinicians. Uses TEAM entries that have a dedicated profile `href`.
 * Kept intentionally low-key so it complements (not competes with) the
 * main hero + Recovery Path narrative.
 */
export default function MeetTheSpecialists() {
  const featured = TEAM.filter((m) => m.href).slice(0, 2);
  if (featured.length === 0) return null;

  return (
    <section className="container-wide py-16">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Sparkles size={12} /> Meet the specialists
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-brand-ink leading-tight max-w-2xl">
            Care led by clinicians you can put a name — and a story — to.
          </h2>
        </div>
        <Link to="/team" className="btn-outline text-sm" data-testid="home-team-see-all">
          See the team <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {featured.map((m) => (
          <motion.div key={m.slug} {...CARD_ANIM}>
            <Link
              to={m.href}
              data-testid={`home-specialist-${m.slug}`}
              className="card-soft overflow-hidden group hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] transition-shadow block"
            >
              <div className="flex items-stretch">
                <div className="w-36 md:w-44 shrink-0 bg-brand-cream overflow-hidden">
                  <img
                    src={m.photo}
                    alt={m.name}
                    className="w-full h-full object-cover aspect-[3/4]"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 p-5 md:p-6 min-w-0">
                  <div className="font-heading text-lg md:text-xl text-brand-ink group-hover:text-brand-olive leading-tight">
                    {m.name}
                  </div>
                  <div className="text-xs md:text-sm text-brand-terracotta mt-1">
                    {m.title}
                  </div>
                  <p className="text-sm text-brand-ink2 mt-3 line-clamp-3">{m.bio}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {m.focus.slice(0, 3).map((f) => (
                      <span
                        key={f}
                        className="text-[10px] md:text-xs bg-brand-cream text-brand-olive rounded-full px-2 py-0.5"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-brand-olive group-hover:gap-2 transition-all">
                    View profile <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
